// Iter75 — Operator Guide Tile.
//
// PURELY STATIC, READ-ONLY help surface on the Dashboard. Summarises
// the long-form operator manual at
//   /app/docs/ARXMENTIS_INCOME_ENGINE_USER_MANUAL.md
// and surfaces the cockpit's safety doctrine and module map in-tile
// so the operator never has to leave the page to remember which
// scanner fits which market condition.
//
// HARD CONSTRAINTS (asserted by Iter75 tests):
//  * NO outbound network primitives. No `import axios`. No
//    `fetch(`. No `XMLHttpRequest`. No `navigator.sendBeacon`.
//  * NO React side-effect hooks. No `useEffect`.
//  * NO `import { API } from "@/App"` — URL construction is
//    impossible.
//  * NO reference to any backend endpoint, scanner endpoint,
//    preview endpoint, journal endpoint, broker endpoint, account
//    endpoint, position endpoint, balance endpoint, or
//    modules-status endpoint.
//  * NO strategy logic, no ranking tokens, no winner / best /
//    select-best / orchestration / auto-* tokens.
//  * The single useState hook controls the local collapse state of
//    the tile. Nothing else.

import React, { useState } from "react";
import {
  BookOpen, ChevronDown, ChevronUp, ShieldCheck, ShieldAlert,
  Compass, Workflow, FileJson, History, AlertTriangle, Map,
} from "lucide-react";

const MANUAL_PATH = "/app/docs/ARXMENTIS_INCOME_ENGINE_USER_MANUAL.md";

const MODULE_INVENTORY = [
  { id: "WHEEL_ITM",     label: "Wheel ITM / CRSP",
    role: "Flagship income engine (protected logic)" },
  { id: "BPCS",          label: "BPCS",
    role: "Bull Put Credit Spread — defined-risk bullish-neutral" },
  { id: "BCCS",          label: "BCCS",
    role: "Bear Call Credit Spread — defined-risk bearish-neutral" },
  { id: "IRON_CONDOR",   label: "Iron Condor",
    role: "4-leg defined-risk neutral / range-harvest" },
  { id: "CFM_CC_SHARES", label: "CFM-CC-SHARES",
    role: "Cash Flow Machine on real share inventory" },
  { id: "CFM_CC_LEAPS",  label: "CFM-CC-LEAPS / PMCC",
    role: "Cash Flow Machine on synthetic LEAPS inventory" },
  { id: "NO_TRADE",      label: "NO_TRADE",
    role: "Valid capital preservation — not a failure" },
];

const MARKET_CONDITION_MAP = [
  { condition: "Flagship opportunity",          module: "Wheel ITM / CRSP" },
  { condition: "Support acceptance",            module: "BPCS"             },
  { condition: "Resistance rejection",          module: "BCCS"             },
  { condition: "Value compression / range",     module: "Iron Condor"      },
  { condition: "Real inventory management",     module: "CFM-CC-SHARES"    },
  { condition: "Synthetic inventory / PMCC",    module: "CFM-CC-LEAPS"     },
  { condition: "Event risk / unclear",          module: "NO_TRADE"         },
];

const DAILY_WORKFLOW = [
  "Start with Wheel ITM / CRSP.",
  "If Wheel is not actionable, identify the market condition.",
  "Use BPCS for support acceptance.",
  "Use BCCS for resistance rejection.",
  "Use Iron Condor for value compression / range.",
  "Use CFM-CC-SHARES when managing real share inventory.",
  "Use CFM-CC-LEAPS when managing synthetic inventory / PMCC.",
  "Use NO_TRADE when structure is unclear, data is incomplete, or risk is not compensated.",
];

const STATUS_GLOSSARY = [
  { code: "ENTER",            note: "Full entry criteria met." },
  { code: "WATCH",            note: "Structure forming, not actionable yet." },
  { code: "BLOCKED",          note: "Hard gate failed (e.g. bar not closed)." },
  { code: "NO_TRADE",         note: "Clean evaluation, no actionable structure. Stand down." },
  { code: "ERROR",            note: "Payload malformed or missing." },
  { code: "DEFEND",           note: "CFM-Shares — active defensive management." },
  { code: "BUYBACK",          note: "Short call near max profit — buyback candidate." },
  { code: "ROLL_OUT_UP",      note: "Short call should be rolled out + up." },
  { code: "ALLOW_ASSIGNMENT", note: "CFM-Shares — assignment acceptable vs. basis." },
  { code: "ASSIGNMENT_RISK",  note: "CFM-LEAPS — short call at assignment risk. Diagnostic." },
  { code: "DEFEND_LEAPS",     note: "CFM-LEAPS — long LEAPS itself needs attention." },
  { code: "CLOSE_DIAGONAL",   note: "CFM-LEAPS — full diagonal close is the cleanest path." },
];

const SAFETY_DOCTRINE = [
  "Wheel ITM remains flagship.",
  "Premium is compensation for risk, not protection by itself.",
  "Defined-risk alternatives are used only when the market condition fits.",
  "CFM-Shares is real inventory.",
  "CFM-LEAPS / PMCC is synthetic inventory, not shares.",
  "Assignment risk in PMCC is diagnostic only.",
  "Cash is a valid position.",
  "NO_TRADE is discipline, not failure.",
];

const NOT_YET = [
  "No automatic all-market scan.",
  "No all-strategy orchestration.",
  "No cross-module ranking.",
  "No auto-select best trade.",
  "No trade placement.",
  "No journal writes from toolbox scanners.",
  "No snapshot database persistence.",
  "No backend replay engine.",
  "No broker / account / position live-data pull for toolbox scanners.",
];

const FUTURE_PHASES = [
  "QA / Production Hardening pass.",
  "UI polish.",
  "Status legend strip.",
  "Optional snapshot-workflow improvements.",
  "Later — explicit journal save.",
  "Later — controlled orchestrator only after strong rules.",
  "Execution layer remains explicitly deferred.",
];

const SNAPSHOT_WORKFLOW = [
  "Run scanner manually.",
  "Review result.",
  "Copy JSON if needed.",
  "Download Snapshot for audit.",
  "Replay snapshot in Snapshot Import / Replay Viewer.",
  "Compare two snapshots in JSON Snapshot Diff Helper.",
  "No database persistence yet.",
];

export default function OperatorGuideTile() {
  const [open, setOpen] = useState(false);

  return (
    <div
      data-testid="operator-guide-tile"
      className="rounded-sm border border-[#26262B] bg-[#0A0A0E] p-3"
    >
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        data-testid="operator-guide-toggle"
        className="w-full flex items-center justify-between text-left"
      >
        <span className="flex items-center gap-2 text-[#00FF88]">
          <BookOpen size={14} />
          <span className="text-[12px] font-mono uppercase tracking-[0.18em]">
            Operator Guide / User Manual v1
          </span>
          <span className="text-[9px] text-[#5F5F66] uppercase tracking-[0.18em]">
            · Iter75 · static · read-only
          </span>
        </span>
        {open
          ? <ChevronUp size={14} className="text-[#8A8A93]" />
          : <ChevronDown size={14} className="text-[#8A8A93]" />}
      </button>

      {open && (
        <div className="mt-3 space-y-4">
          {/* Safety banner */}
          <div
            data-testid="operator-guide-banner"
            className="flex flex-wrap items-center gap-2 text-[9px] font-mono uppercase tracking-[0.14em] text-[#8A8A93]"
          >
            <ShieldAlert size={11} className="text-[#FFB800]" />
            <span data-testid="operator-guide-banner-read-only">
              Read-only decision cockpit.
            </span>
            <span data-testid="operator-guide-banner-no-auto-trading">
              · No auto-trading.
            </span>
            <span data-testid="operator-guide-banner-no-execution">
              · No trade execution.
            </span>
            <span data-testid="operator-guide-banner-no-ranking">
              · No cross-module ranking.
            </span>
            <span data-testid="operator-guide-banner-no-best">
              · No auto-select best trade.
            </span>
            <span data-testid="operator-guide-banner-no-journal">
              · No journal writes from toolbox scanners.
            </span>
            <span data-testid="operator-guide-banner-no-broker">
              · No broker / account live-data fetch from toolbox scanners.
            </span>
          </div>

          {/* System overview */}
          <Section
            testid="operator-guide-overview"
            icon={<ShieldCheck size={11} className="text-[#00FF88]" />}
            title="1 · System overview"
          >
            <p className="text-[11px] font-mono text-[#E0E0E0] leading-relaxed">
              ARXMENTIS INCOME ENGINE is a read-only income strategy
              decision cockpit. It is not an auto-trading system. It
              does not place trades. It does not rank all strategies
              automatically. It does not auto-select the best trade.
              It does not write journal / database records from
              toolbox scanners. It does not fetch broker / account /
              position data inside toolbox scanners.
            </p>
          </Section>

          {/* Current module inventory */}
          <Section
            testid="operator-guide-modules"
            icon={<Compass size={11} className="text-[#00FF88]" />}
            title="2 · Current module inventory"
          >
            <ul className="space-y-1">
              {MODULE_INVENTORY.map(m => (
                <li
                  key={m.id}
                  data-testid={`operator-guide-module-${m.id}`}
                  className="flex items-baseline gap-2 text-[11px] font-mono"
                >
                  <span className="text-[#00FF88] uppercase tracking-[0.12em] min-w-[10rem]">
                    {m.label}
                  </span>
                  <span className="text-[#8A8A93]">{m.role}</span>
                </li>
              ))}
            </ul>
          </Section>

          {/* Scanner behavior */}
          <Section
            testid="operator-guide-scanner-behavior"
            icon={<Workflow size={11} className="text-[#00FF88]" />}
            title="3 · Current scanner behavior"
          >
            <ul className="list-disc ml-5 space-y-0.5 text-[11px] font-mono text-[#E0E0E0]">
              <li>Each scanner is module-specific.</li>
              <li>Each scanner is manually run.</li>
              <li>Each scanner evaluates one strategy module at a time.</li>
              <li>Each scanner can evaluate a watchlist only for its own module.</li>
              <li>There is no all-module scanner yet.</li>
              <li>There is no strategy orchestrator yet.</li>
              <li>There is no best-strategy selector yet.</li>
            </ul>
          </Section>

          {/* Daily workflow */}
          <Section
            testid="operator-guide-daily-workflow"
            icon={<Workflow size={11} className="text-[#00FF88]" />}
            title="4 · Daily operating workflow"
          >
            <ol className="list-decimal ml-5 space-y-0.5 text-[11px] font-mono text-[#E0E0E0]">
              {DAILY_WORKFLOW.map((line, i) => (
                <li
                  key={i}
                  data-testid={`operator-guide-daily-workflow-${i}`}
                >
                  {line}
                </li>
              ))}
            </ol>
          </Section>

          {/* Market-condition map */}
          <Section
            testid="operator-guide-market-condition-map"
            icon={<Map size={11} className="text-[#00FF88]" />}
            title="5 · Market-condition map"
          >
            <ul className="space-y-1">
              {MARKET_CONDITION_MAP.map(row => (
                <li
                  key={row.condition}
                  className="flex items-baseline gap-2 text-[11px] font-mono"
                >
                  <span className="text-[#8A8A93] min-w-[18rem]">
                    {row.condition}
                  </span>
                  <span className="text-[#FFB800]">→</span>
                  <span className="text-[#E0E0E0]">{row.module}</span>
                </li>
              ))}
            </ul>
          </Section>

          {/* Payload workflow */}
          <Section
            testid="operator-guide-payload-workflow"
            icon={<FileJson size={11} className="text-[#00FF88]" />}
            title="6 · Payload workflow"
          >
            <ol className="list-decimal ml-5 space-y-0.5 text-[11px] font-mono text-[#E0E0E0]">
              <li>Use Payload Builder v1 to generate scanner-ready JSON.</li>
              <li>Review generated JSON before running scanner.</li>
              <li>Copy generated JSON.</li>
              <li>Paste into the target scanner panel.</li>
              <li>Run one scanner manually.</li>
              <li>Do not assume generated payload means trade approval.</li>
            </ol>
          </Section>

          {/* Status glossary */}
          <Section
            testid="operator-guide-status-glossary"
            icon={<AlertTriangle size={11} className="text-[#FFB800]" />}
            title="7 · Scanner result interpretation"
          >
            <ul className="grid grid-cols-1 lg:grid-cols-2 gap-x-4 gap-y-1">
              {STATUS_GLOSSARY.map(s => (
                <li
                  key={s.code}
                  data-testid={`operator-guide-status-${s.code}`}
                  className="flex items-baseline gap-2 text-[11px] font-mono"
                >
                  <span className="text-[#E0E0E0] uppercase tracking-[0.12em] min-w-[11rem]">
                    {s.code}
                  </span>
                  <span className="text-[#8A8A93]">{s.note}</span>
                </li>
              ))}
            </ul>
          </Section>

          {/* Snapshot workflow */}
          <Section
            testid="operator-guide-snapshot-workflow"
            icon={<History size={11} className="text-[#00FF88]" />}
            title="8 · Snapshot workflow"
          >
            <ol className="list-decimal ml-5 space-y-0.5 text-[11px] font-mono text-[#E0E0E0]">
              {SNAPSHOT_WORKFLOW.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ol>
          </Section>

          {/* What the system does NOT do yet */}
          <Section
            testid="operator-guide-not-yet"
            icon={<ShieldAlert size={11} className="text-[#FF3366]" />}
            title="9 · What the system does NOT do yet"
          >
            <ul className="list-disc ml-5 space-y-0.5 text-[11px] font-mono text-[#E0E0E0]">
              {NOT_YET.map((line, i) => (
                <li
                  key={i}
                  data-testid={`operator-guide-not-yet-${i}`}
                >
                  {line}
                </li>
              ))}
            </ul>
          </Section>

          {/* Safety doctrine */}
          <Section
            testid="operator-guide-safety-doctrine"
            icon={<ShieldCheck size={11} className="text-[#00FF88]" />}
            title="10 · Safety doctrine"
          >
            <ul className="list-disc ml-5 space-y-0.5 text-[11px] font-mono text-[#E0E0E0]">
              {SAFETY_DOCTRINE.map((line, i) => (
                <li
                  key={i}
                  data-testid={`operator-guide-safety-${i}`}
                >
                  {line}
                </li>
              ))}
            </ul>
          </Section>

          {/* Future phases */}
          <Section
            testid="operator-guide-future-phases"
            icon={<Compass size={11} className="text-[#5F5F66]" />}
            title="11 · Suggested future phases"
          >
            <ul className="list-disc ml-5 space-y-0.5 text-[11px] font-mono text-[#8A8A93]">
              {FUTURE_PHASES.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          </Section>

          {/* Manual reference */}
          <div
            data-testid="operator-guide-manual-reference"
            className="text-[10px] font-mono text-[#5F5F66] uppercase tracking-[0.12em]"
          >
            Full manual: <span className="text-[#E0E0E0] normal-case tracking-normal">{MANUAL_PATH}</span>
          </div>

          {/* Footer disclaimer */}
          <div
            data-testid="operator-guide-disclaimer"
            className="text-[9px] font-mono text-[#5F5F66] uppercase tracking-[0.12em]"
          >
            Read-only documentation surface. No backend was contacted
            to render this tile. No order was placed. No journal
            record was created. Not financial advice.
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ testid, icon, title, children }) {
  return (
    <section
      data-testid={testid}
      className="rounded-sm border border-[#26262B] bg-[#08080A] p-3"
    >
      <h3 className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.18em] text-[#00FF88] mb-2">
        {icon}
        <span>{title}</span>
      </h3>
      {children}
    </section>
  );
}
